import store from '@/store'

// Icons
import MetaPlatformIcon from '../../components/globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../../components/globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import MonoMetaIcon from '../../components/globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoTikTokIcon from '../../components/globals/Icons/socials/mono/MonoTiktokIcon.vue'

const platformIconMap = {
  'meta': MetaPlatformIcon,
  'tiktok': TikTokPlatformIcon,
  'mono-meta': MonoMetaIcon,
  'mono-tiktok': MonoTikTokIcon
}
const platformNameMap = {
  'meta': 'Meta',
  'tiktok': 'TikTok'
}
const platformGoalMetricMap = {
  'meta': 'goal_metric',
  'tiktok': 'tiktok_goal_metric'
}
// Maps between the keys used for the frontend and backend
const providerKeyMap = {
  'meta': 'facebook',
  'tiktok': 'tiktok'
}
const reverseProviderKeyMap = Object.fromEntries(Object.entries(providerKeyMap).map(([key, value]) => [value, key]))

const formatAdAccountsByPlatform = (allProviders, excludeDuplicateAccounts = true, excludeGuestAccounts = true) => {
  // Accepts the data returned by the LensAPI.Lens.getPlatformAccounts() method (/providers)
  // Returns an object with the ad accounts structured by provider
  const adAccountOptions = {
    meta: [],
    tiktok: []
  }
  for (const providerKey in adAccountOptions) {
    const platformProviders = [...allProviders].filter(provider => 
      provider.provider === providerKeyMap[providerKey]
      && (!excludeGuestAccounts || provider.access_type !== 'guest')
    )
    adAccountOptions[providerKey] = platformProviders.flatMap(provider => provider?.advertisers?.map(adAccount => ({
      ...adAccount,
      companyName: provider.business_name,
      businessId: provider.business_id,
      platform: providerKey,
      serverProviderKey: provider.provider,
      accessType: provider.access_type
    })) ?? [])
    if (excludeDuplicateAccounts) {
      adAccountOptions[providerKey] = deduplicateAdAccounts(adAccountOptions[providerKey])
    }
  }
  return adAccountOptions
}

const deduplicateAdAccounts = (adAccounts) => {
  const seen = new Set()
  return adAccounts.filter(adAccount => {
    const id = adAccount?.id
    if (id === null) return true
    if (seen.has(id)) return false
    seen.add(id)
    return true
  })
}

const formatProvidersByPlatform = (allProviders) => {
  const platformAccounts = {
    meta: [],
    tiktok: []
  }
  for (const providerKey in platformAccounts) {
    const platformProviders = [...allProviders].filter(provider => provider.provider === providerKeyMap[providerKey])
    platformAccounts[providerKey] = platformProviders.map(provider => ({
      name: provider.business_name,
      id: provider.business_id,
      connected: provider.is_valid,
      email: provider.email,
      createdBy: provider.user_id,
      adAccounts: provider?.advertisers ?? [],
      platform: providerKey,
      accessType: provider.access_type
    }))
  }
  return platformAccounts
}

const getOnlyConnectedAdAccounts = (allProviders, connectedAdAccountIds) => {
  // Accepts the data returned by the LensAPI.Lens.getPlatformAccounts() method (/providers) and an array of connected ad account IDs
  // Returns an array of ad accounts that are connected given the provided id array.
  const allAdAccounts = [...allProviders].flatMap(provider => provider?.advertisers?.map(adAccount => ({
    ...adAccount,
    companyName: provider.business_name,
    platform: reverseProviderKeyMap[provider.provider],
  })) ?? [])
  const connectedAdAccounts = allAdAccounts.filter(adAccount => connectedAdAccountIds.includes(adAccount.id))
  return connectedAdAccounts
}

const enrichLensAdAccountsWithProviderData = (allProviders, lensBrandAdAccounts) => {
  // Accepts the data returned by the LensAPI.Lens.getPlatformAccounts() method (/providers) and the `accounts` array from a Lens object (getSidebarLenses)
  // Returns an array of ad accounts with the provider data enriched
  const allAdAccounts = [...allProviders].flatMap(provider => provider?.advertisers?.map(adAccount => ({
    ...adAccount,
    companyName: provider.business_name,
    platform: reverseProviderKeyMap[provider.provider],
  })) ?? [])
  return lensBrandAdAccounts.map(lensAdAccount => {
    const providerAdAccount = allAdAccounts.find(adAccount => adAccount.id === lensAdAccount.account_id)
    let account = {
      id: lensAdAccount.account_id,
      name: lensAdAccount.account_name,
      platform: reverseProviderKeyMap[lensAdAccount.provider],
      serverProviderKey: lensAdAccount.provider,
      businessId: lensAdAccount.business_id
    }
    if (providerAdAccount) {
      account = {
        ...account,
        companyName: providerAdAccount.companyName,
        currency: providerAdAccount.currency,
      }
    }
    return account
  })
}

const getGoalMetricKey = (lens, overridePlatform = null) => {
  const platform = overridePlatform ?? store.getters['MetricsModule/getMetricsPlatform']
  return lens?.[platformGoalMetricMap[platform]] ?? 'roas'
}

export {
  formatAdAccountsByPlatform,
  formatProvidersByPlatform,
  getOnlyConnectedAdAccounts,
  enrichLensAdAccountsWithProviderData,
  getGoalMetricKey,
  providerKeyMap,
  reverseProviderKeyMap,
  platformIconMap,
  platformNameMap
}